import React, { useState } from 'react';
import CompanySettings from './CompanySettings';
import ManageSubscription from './ManageSubscription';
import NotificationSettings from './NotificationSettings';
import SecuritySettings from './SecuritySettings';
import Troubleshooting from './Troubleshooting';
import SystemInfo from './SystemInfo';
import { Building2, CreditCard, Bell, Shield, Wrench, Info } from 'lucide-react';
import { normalizeRole, ROLES } from '../../../../config/roles';

const SettingsView = () => {
    const [activeSection, setActiveSection] = useState('company');

    const currentRole = normalizeRole(sessionStorage.getItem('selectedRole'));
    const isSystemAdmin = currentRole === ROLES.SYSTEM_ADMIN.code;

    const allMenuItems = [
        { id: 'company', label: 'Company Settings', icon: Building2, color: 'text-blue-600', bgColor: 'bg-blue-50' },
        { id: 'subscription', label: 'Manage Subscription', icon: CreditCard, color: 'text-green-600', bgColor: 'bg-green-50' },
        // { id: 'notifications', label: 'Notification Settings', icon: Bell, color: 'text-orange-600', bgColor: 'bg-orange-50' }, // Hidden since notifications are not implemented
        { id: 'security', label: 'Security Settings', icon: Shield, color: 'text-red-600', bgColor: 'bg-red-50' },
        { id: 'troubleshooting', label: 'Troubleshooting', icon: Wrench, color: 'text-gray-600', bgColor: 'bg-gray-100', systemAdminOnly: true },
        { id: 'system', label: 'System Information', icon: Info, color: 'text-indigo-600', bgColor: 'bg-indigo-50', systemAdminOnly: true },
    ];

    const menuItems = allMenuItems.filter(item => !item.systemAdminOnly || isSystemAdmin);

    const renderContent = () => {
        switch (activeSection) {
            case 'company':
                return <CompanySettings />;
            case 'subscription':
                return <ManageSubscription />;
            case 'notifications':
                return <NotificationSettings />;
            case 'security':
                return <SecuritySettings />;
            case 'troubleshooting':
                return <Troubleshooting />;
            case 'system':
                return <SystemInfo />;
            default:
                return <CompanySettings />;
        }
    };

    return (
        <div className="flex flex-col md:flex-row h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-white overflow-hidden">
            {/* Sidebar (Desktop only) */}
            <div className="hidden md:flex w-64 bg-gradient-to-b from-purple-100/50 to-blue-100/50 backdrop-blur-sm border-r border-purple-200/30 flex-col flex-shrink-0">
                {/* Header */}
                <div className="p-4 border-b border-purple-200/30">
                    <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                        Settings
                    </h1>
                </div>

                {/* Menu Items */}
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto custom-scrollbar">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeSection === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveSection(item.id)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${isActive
                                    ? `${item.bgColor} ${item.color} shadow-md scale-[1.02]`
                                    : 'text-gray-600 hover:bg-white/50 hover:shadow-sm'
                                    }`}
                            >
                                <Icon size={20} className={isActive ? item.color : 'text-gray-400'} />
                                <span className={`text-sm font-semibold ${isActive ? '' : 'font-medium'}`}>
                                    {item.label}
                                </span>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Mobile Navigation Tabs (Mobile only) */}
            <div className="md:hidden flex overflow-x-auto no-scrollbar bg-white border-b border-gray-100 p-2 gap-1.5 flex-shrink-0">
                {menuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    return (
                        <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${isActive
                                ? `${item.bgColor} ${item.color} shadow-sm border border-purple-200/10`
                                : 'text-gray-500 hover:bg-gray-50'
                                }`}
                        >
                            <Icon size={14} className={isActive ? item.color : 'text-gray-400'} />
                            <span>{item.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-hidden">
                {renderContent()}
            </div>
        </div>
    );
};

export default SettingsView;
